import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, Loader2, Trash2, PenLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg, API_BASE } from "@/lib/api";

const field = "bg-slate-950/60 border-slate-700";
export const photoUrl = (u) => (u?.photo_v ? `${API_BASE}/public/staff-photo/${u.id}?v=${u.photo_v}` : "");

export const SignaturePreview = ({ u, company }) => (
  <div className="rounded-sm bg-[#f5f2ea] text-slate-800 p-5" data-testid="signature-preview">
    <p className="text-sm mb-2">Sincerely,</p>
    {u.signature_v && <img src={`${API_BASE}/users/${u.id}/signature?v=${u.signature_v}`} alt="" className={`h-12 w-auto mb-1 ${photoUrl(u) ? "ml-[72px]" : ""}`} data-testid="signature-preview-hand" />}
    <div className="flex gap-4 items-start">
      {photoUrl(u) && <img src={photoUrl(u)} alt={u.name} className="h-14 w-14 rounded-full object-cover" />}
      <div className="text-sm leading-relaxed"><p className="font-semibold">{u.name}</p>{u.title && <p className="text-slate-500">{u.title}</p>}<p>{company}</p></div>
    </div>
  </div>
);

export const ProfileForm = ({ user, company, onSaved }) => {
  const [f, setF] = useState({ name: user.name || "", title: user.title || "", phone: user.phone || "" });
  const [u, setU] = useState(user);
  const [busy, setBusy] = useState("");
  const file = useRef(null);
  const sigFile = useRef(null);
  useEffect(() => { setU(user); setF({ name: user.name || "", title: user.title || "", phone: user.phone || "" }); }, [user]);
  const done = (data, msg) => { setU(data); onSaved?.(data); toast.success(msg); };
  const run = async (key, fn, msg) => {
    setBusy(key);
    try { done((await fn()).data, msg); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const save = (e) => { e.preventDefault(); run("save", () => api.put(`/users/${u.id}/profile`, f), "Profile saved"); };
  const upload = (e, kind = "photo") => {
    const fl = e.target.files?.[0];
    e.target.value = "";
    if (!fl) return;
    const fd = new FormData();
    fd.append("file", fl);
    run(kind, () => api.post(`/users/${u.id}/${kind}`, fd), kind === "photo" ? "Photo updated" : "Signature updated");
  };
  return (
    <div className="grid md:grid-cols-2 gap-8" data-testid="profile-form">
      <form onSubmit={save} className="space-y-4">
        {[["name", "Full name", true], ["title", "Title, e.g. Senior Analyst"], ["phone", "Direct phone"]].map(([k, l, req]) => (
          <div key={k} className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-slate-400">{l}</Label>
            <Input required={req} maxLength={k === "phone" ? 40 : 80} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={field} data-testid={`profile-${k}`} />
          </div>
        ))}
        <div className="flex flex-wrap gap-2 pt-2">
          <Button type="submit" disabled={!!busy} className="rounded-full bg-emerald-500 text-emerald-950 hover:bg-emerald-400" data-testid="profile-save">{busy === "save" && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Save profile</Button>
          <input ref={file} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={upload} data-testid="profile-photo-input" />
          <Button type="button" variant="outline" disabled={!!busy} onClick={() => file.current?.click()} className="rounded-full border-slate-700 bg-transparent" data-testid="profile-photo-upload">{busy === "photo" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Camera className="h-4 w-4 mr-1" />}{u.photo_v ? "Change photo" : "Add photo"}</Button>
          {u.photo_v && <Button type="button" variant="ghost" disabled={!!busy} onClick={() => run("del", () => api.delete(`/users/${u.id}/photo`), "Photo removed")} className="text-rose-300" data-testid="profile-photo-remove"><Trash2 className="h-4 w-4 mr-1" />Remove photo</Button>}
        </div>
        <div className="pt-4 border-t border-slate-800 space-y-2" data-testid="profile-signature-section">
          <p className="text-xs uppercase tracking-wider text-slate-400">Handwritten signature</p>
          <p className="text-xs text-slate-500">Sign on white paper, photograph or scan it, and upload a PNG (a transparent background looks best). It appears above your name on cover letters and lender letters.</p>
          {u.signature_v && <div className="rounded bg-white p-3 inline-block"><img src={`${API_BASE}/users/${u.id}/signature?v=${u.signature_v}`} alt="Signature" className="h-14 w-auto" data-testid="profile-signature-img" /></div>}
          <div className="flex flex-wrap gap-2">
            <input ref={sigFile} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => upload(e, "signature")} data-testid="profile-signature-input" />
            <Button type="button" variant="outline" disabled={!!busy} onClick={() => sigFile.current?.click()} className="rounded-full border-slate-700 bg-transparent" data-testid="profile-signature-upload">{busy === "signature" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <PenLine className="h-4 w-4 mr-1" />}{u.signature_v ? "Replace signature" : "Upload signature"}</Button>
            {u.signature_v && <Button type="button" variant="ghost" disabled={!!busy} onClick={() => run("delsig", () => api.delete(`/users/${u.id}/signature`), "Signature removed")} className="text-rose-300" data-testid="profile-signature-remove"><Trash2 className="h-4 w-4 mr-1" />Remove</Button>}
          </div>
        </div>
      </form>
      <div>
        <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">How your signature appears</p>
        <SignaturePreview u={{ ...u, ...f }} company={company} />
        <p className="text-xs text-slate-500 mt-3">Shown at the end of studies, lender letters, cover letters and client emails, unless the client has asked to omit it.</p>
      </div>
    </div>
  );
};
